import L from "leaflet";

/** `L.TileLayer.WMS` que sabe repedir un subconjunto de sus tiles.
 *
 *  Vive en su propio módulo porque lo comparten las capas WMS del paso 3
 *  (`AoiWmsLayer`) y las del paso 4 (`LotesWmsLayer`): las dos necesitan lo
 *  mismo —repintar el área que cambió sin tirar la vista entera— y copiarlo
 *  habría dejado dos versiones del mismo truco sobre las internas de Leaflet,
 *  que es justo lo que no conviene duplicar.
 */
export const SelectiveWms = L.TileLayer.WMS.extend({
  /* NO poner acá opciones de tiling sin probarlas contra un cambio de zoom.
     Dos intentos de acortar la transición del WMS rompieron el descarte del
     nivel anterior —se veían los tiles de z10 todavía en pantalla al llegar a
     z16, con las etiquetas y los polígonos duplicados— y los dos se
     revirtieron:

       - `updateWhenZooming: false`, que es lo que la documentación propone para
         no pedir los niveles intermedios. En `GridLayer._setView`,
         `tileZoomChanged` queda siempre en `false` con esa opción, y el zoom
         animado llama a `_setView` con `noUpdate = true`: el bloque entero se
         saltea y `_pruneTiles()` nunca corre.

       - `tileSize: 512`, medido como 4 veces menos peticiones al mismo costo
         cada una (25 ms contra 26 ms por GetMap contra este GeoServer). Con
         tiles del doble de lado la grilla efectiva de la capa queda un nivel más
         gruesa, y el nivel viejo seguía quedando en el mapa — sobre todo en los
         dos zooms más altos, pegado al `maxZoom: 20` de estas capas.

     El costo de la transición es real y sigue pendiente. Los caminos que no
     tocan el descarte: `updateWhenIdle: true` (sólo saca el handler de `move`
     de `getEvents`) y cachear del lado de GeoServer. Cualquiera de los dos se
     prueba cambiando de zoom repetidas veces en los niveles altos antes de
     darlo por bueno. */

  /** `ticketMapa` NO es un parámetro de WMS: declararla acá, entre las
   *  opciones propias de la capa, es lo que evita que Leaflet la mande en la
   *  URL (todo lo que no es opción de la capa termina en el GetMap). Es la
   *  función que devuelve el ticket vigente del registro, o `""` si todavía no
   *  llegó (ver `ticketMapaRegistro`). */
  options: { ticketMapa: null },

  /** Margen en píxeles alrededor del área que cambió. El borde del polígono
   *  tiene grosor: un vértice justo sobre el límite de un tile pinta unos
   *  píxeles del vecino, que si no quedaría sucio. */
  _MARGEN_PX: 24,

  /** Sin ticket no se pide ningún tile: la Api lo rechazaría. Las capas
   *  repintan cuando el ticket llega (`AoiWmsLayer`/`LotesWmsLayer.addTo`). */
  _isValidTile(coords: L.Coords): boolean {
    const ticketMapa = (this as unknown as { options: { ticketMapa: (() => string) | null } })
      .options.ticketMapa;
    if (ticketMapa && !ticketMapa()) return false;
    return (
      L.TileLayer.WMS.prototype as unknown as { _isValidTile(c: L.Coords): boolean }
    )._isValidTile.call(this, coords);
  },

  refrescarBounds(bounds: L.LatLngBounds): number {
    const map = (this as unknown as { _map: L.Map })._map;
    const self = this as unknown as {
      _tileZoom?: number;
      _tiles: Record<string, { current: boolean; el: HTMLImageElement; coords: L.Coords }>;
      _tileCoordsToBounds(c: L.Coords): L.LatLngBounds;
      getTileUrl(c: L.Coords): string;
      _version: number;
      _MARGEN_PX: number;
    };
    const zoom = self._tileZoom;
    if (!map || zoom === undefined) return 0;

    // El área llega en grados; se pasa a píxeles para agrandarla en una medida
    // que signifique lo mismo en cualquier zoom, y se vuelve a grados.
    const m = self._MARGEN_PX;
    const sw = map.project(bounds.getSouthWest(), zoom);
    const ne = map.project(bounds.getNorthEast(), zoom);
    const p1 = L.point(Math.min(sw.x, ne.x) - m, Math.min(sw.y, ne.y) - m);
    const p2 = L.point(Math.max(sw.x, ne.x) + m, Math.max(sw.y, ne.y) + m);
    const objetivo = L.latLngBounds(map.unproject(p1, zoom), map.unproject(p2, zoom));

    // Sube UNA vez por refresco, no una por tile: los tiles repedidos juntos
    // comparten valor y los que no se tocan conservan el anterior, así el
    // navegador cachea lo que no cambió.
    self._version = (self._version || 0) + 1;

    let pedidos = 0;
    for (const clave in self._tiles) {
      const tile = self._tiles[clave];
      if (!tile.current || !tile.el || tile.coords.z !== zoom) continue;
      if (!self._tileCoordsToBounds(tile.coords).intersects(objetivo)) continue;
      // **`tile.loaded` NO se toca**, y es importante.
      //
      // Acá se ponía en `null` para marcarlo como pendiente mientras la imagen
      // nueva viajaba. El problema es lo que Leaflet hace con ese campo: un tile
      // con `loaded` falsy nunca pasa a `active` (`_updateOpacity` lo saltea con
      // un `continue`), y en `_pruneTiles` un tile `current && !active` RETIENE a
      // su padre — el nivel de zoom anterior. Al hacer zoom IN el nivel anterior
      // es justamente el padre, así que un solo tile que no vuelva a cargar (una
      // petición abortada, un error de GeoServer) dejaba los tiles del nivel
      // viejo congelados en pantalla para siempre. Al hacer zoom OUT no pasaba:
      // ahí el nivel anterior son los hijos, que se retienen con menos
      // prioridad.
      //
      // Dejarlo con su valor viejo no pierde nada: el `load` de la imagen nueva
      // lo reescribe con su propia marca de tiempo, y mientras tanto el tile se
      // sigue viendo (es la imagen anterior del MISMO lugar, no un hueco).
      tile.el.src = self.getTileUrl(tile.coords);
      pedidos++;
    }
    return pedidos;
  },

  /** Al terminar un zoom se tira la grilla entera y se vuelve a pedir.
   *
   *  **Por qué hace falta.** Leaflet conserva los tiles del nivel anterior
   *  mientras alguno del nivel nuevo siga sin estar `active`, para no dejar un
   *  hueco. Al hacer zoom IN el nivel anterior es el PADRE de los tiles nuevos,
   *  y `_pruneTiles` lo retiene explícitamente (`_retainParent`, hasta 5
   *  niveles arriba); si un solo tile nuevo no llega a `active` —una petición
   *  abortada, un error— ese padre queda en pantalla para siempre. Es lo que se
   *  veía: el mismo lote y la misma etiqueta dos veces, una grande (nivel viejo,
   *  escalado) y una chica.
   *
   *  Al hacer zoom OUT no pasaba, porque ahí el nivel anterior son los HIJOS,
   *  que se retienen con menos prioridad; y al panear tampoco, porque `_update`
   *  vuelve a marcar qué tiles son actuales y el descarte se completa.
   *
   *  `redraw()` es API pública y hace exactamente lo que se necesita:
   *  `_removeAllTiles()` —que vacía TODOS los niveles, no sólo el actual—,
   *  después `_updateLevels()` quita los contenedores que quedaron sin hijos, y
   *  `_update()` pide los del nivel nuevo. No quedan restos posibles.
   *
   *  **Lo que se paga**: se pierde el nivel viejo como relleno visual mientras
   *  llega el nuevo, así que durante ese instante se ve el satelital sin la capa
   *  en vez de la capa estirada. Con GeoServer respondiendo en ~25 ms por tile
   *  es corto, y es preferible a ver datos duplicados. */
  getEvents(): Record<string, unknown> {
    const eventos = (
      L.TileLayer.WMS.prototype.getEvents as () => Record<string, unknown>
    ).call(this);
    return {
      ...eventos,
      zoomend: (this as unknown as { _alTerminarElZoom: () => void })
        ._alTerminarElZoom,
    };
  },

  _alTerminarElZoom(): void {
    (this as unknown as { redraw(): void }).redraw();
  },

  refrescarTodo(): void {
    const self = this as unknown as { _version: number; redraw(): void };
    self._version = (self._version || 0) + 1;
    self.redraw();
  },

  /** Llama a `alPintar` cuando los tiles que la capa tiene en vuelo terminaron
   *  de cargar — o en el acto si no hay ninguno.
   *
   *  **Se mira la imagen, no el evento `load` de la capa**, y eso es
   *  deliberado: `refrescarBounds` repinta escribiendo `tile.el.src` a mano,
   *  por fuera de la contabilidad de `GridLayer`, así que para esos tiles el
   *  `load` de la capa no se dispara nunca. `HTMLImageElement.complete` sí
   *  distingue los dos casos, y vale igual para los tiles que creó `redraw()`.
   *
   *  Hay que llamarlo DESPUÉS de pedir el refresco: lo que mide es lo que haya
   *  quedado pendiente en ese momento.
   *
   *  Un tile que falla cuenta como terminado: lo que se espera es que deje de
   *  haber un hueco, y un tile roto no se va a llenar esperándolo más. */
  alTerminarDePintar(alPintar: () => void): void {
    const self = this as unknown as {
      _tiles: Record<string, { el: HTMLImageElement }>;
      _ESPERA_VACIA_MS: number;
      _MAX_ESPERAS_VACIAS: number;
    };

    let listo = false;
    let vacias = 0;

    const terminar = (): void => {
      if (listo) return;
      listo = true;
      alPintar();
    };

    const revisar = (): void => {
      if (listo) return;

      const pendientes: HTMLImageElement[] = [];
      let total = 0;
      for (const clave in self._tiles) {
        const el = self._tiles[clave]?.el;
        if (!el) continue;
        total++;
        if (!el.complete) pendientes.push(el);
      }

      if (pendientes.length === 0) {
        // Hay tiles y están todos cargados: la capa ya se ve.
        if (total > 0) {
          terminar();
          return;
        }
        // **Sin un solo tile todavía.** No quiere decir que no haya nada que
        // esperar: quiere decir que `_update` aún no pidió nada. Pasa siempre
        // que la capa se repinta en un momento en que Leaflet no puede armar
        // la grilla — el contenedor todavía sin tamaño (el mapa recién montado
        // dentro de un modal) o un `fitBounds` a mitad de animación. Devolver
        // "pintado" acá es lo que hacía que quien espera destapara el mapa
        // antes de tiempo, así que se vuelve a mirar un rato.
        if (++vacias > self._MAX_ESPERAS_VACIAS) {
          terminar();
          return;
        }
        setTimeout(revisar, self._ESPERA_VACIA_MS);
        return;
      }

      vacias = 0;
      let faltan = pendientes.length;
      for (const el of pendientes) {
        const asentado = () => {
          el.removeEventListener("load", asentado);
          el.removeEventListener("error", asentado);
          // Al asentarse TODA la tanda se vuelve a mirar en vez de dar por
          // terminado: mientras cargaban, el mapa pudo haber sumado tiles
          // (el encuadre que termina, un pane que recién toma tamaño).
          if (--faltan === 0) revisar();
        };
        el.addEventListener("load", asentado);
        el.addEventListener("error", asentado);
      }
    };

    revisar();
  },

  /** Cada cuánto se vuelve a mirar mientras la capa no pidió ningún tile. */
  _ESPERA_VACIA_MS: 100,

  /** Cuántas de esas miradas antes de darse por vencido (~3 s). Quien llama
   *  tiene además su propio tope; esto evita quedarse mirando para siempre una
   *  capa que de verdad no va a pedir nada, como la de un registro sin piezas. */
  _MAX_ESPERAS_VACIAS: 30,

  /** `_v` no es un parámetro de WMS: la Api lo ignora, y al navegador le
   *  alcanza para tratarla como otra URL y no servir su caché.
   *
   *  `TICKET` se agrega en cada tile y no como parámetro fijo de la capa: así
   *  un ticket renovado entra solo en los tiles siguientes, sin repintar. */
  getTileUrl(coords: L.Coords): string {
    const self = this as unknown as {
      _version?: number;
      options: { ticketMapa: (() => string) | null };
    };
    let url = L.TileLayer.WMS.prototype.getTileUrl.call(this, coords);
    if (self.options.ticketMapa) {
      url += `&TICKET=${encodeURIComponent(self.options.ticketMapa())}`;
    }
    return self._version ? `${url}&_v=${self._version}` : url;
  },
}) as unknown as {
  new (
    url: string,
    options: L.WMSOptions & { ticketMapa?: () => string },
  ): L.TileLayer.WMS & {
    refrescarBounds(b: L.LatLngBounds): number;
    refrescarTodo(): void;
    alTerminarDePintar(alPintar: () => void): void;
  };
};

export type SelectiveWms = InstanceType<typeof SelectiveWms>;
